/**
 * Conversation history management for Ragsmith Web UI
 */

import { API_BASE, state, saveSessionStateWithModel, setLastSentMessage, loadNarratorProgress } from './state.js';
import { renderCompactionDivider } from './context-meter.js';
import { clearChat, addMessage, addMetaToMessage, addAnalyticsToMessage, updateModeIndicators, showCollectionHeader, hideCollectionHeader, showImageSearchResults, hasThinkingContent, separateThinkingContent, addThinkingToMessage, attachOrphanDeleteButton, renderFactIngestChip, renderRecordingIngestCard } from './ui.js';
import { loadAgent, loadCollection, loadModel, getHeaders, fetchNarratorProgress } from './api.js';
import {
    updateMenuLoadButton,
    updateHeaderStatus,
    getMenuElements,
    populateMenuAgents,
    findTierByModelName,
    getModelDisplayName,
    getCollectionTitleByName,
    updateAgentDisplay,
    healUnservableModel,
    providerForValue
} from './menu.js';
import { updateCurrentModelDisplay } from './model-manager.js';
import { showToast } from './toast.js';
import { addRetryButton } from './chat.js';
import { initConvFilters, getFilterParams, hasActiveFilters, isFilterLoading, waitForMinSpinner } from './conv-filters.js';

/** Pagination state */
let currentOffset = 0;
let hasMore = false;
let isLoading = false;
const PAGE_SIZE = 15;

/** DOM elements */
let listContainer = null;
let loadMoreBtn = null;
let refreshBtn = null;

/**
 * Format relative time (2h ago, Yesterday, Dec 28, etc.)
 */
function formatRelativeTime(dateString) {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;

    // Format as date
    const options = { month: 'short', day: 'numeric' };
    if (date.getFullYear() !== now.getFullYear()) {
        options.year = 'numeric';
    }
    return date.toLocaleDateString('en-US', options);
}

/**
 * Fetch conversations list from API
 */
async function fetchConversations(offset = 0, limit = PAGE_SIZE) {
    const filterParams = getFilterParams();
    const res = await fetch(`${API_BASE}/sessions/list?limit=${limit}&offset=${offset}${filterParams}`, {
        headers: getHeaders()
    });
    if (!res.ok) throw new Error('Failed to fetch conversations');
    return await res.json();
}

/**
 * Fetch full conversation history
 */
async function fetchConversationHistory(sessionId) {
    const res = await fetch(`${API_BASE}/sessions/${sessionId}`, {
        headers: getHeaders()
    });
    // Carry the status: "Failed to fetch conversation" told the user nothing
    // and told whoever debugged it even less.
    if (!res.ok) throw new Error(`history fetch failed (HTTP ${res.status})`);
    return await res.json();
}

/**
 * Delete a conversation
 */
async function deleteConversationApi(sessionId) {
    const res = await fetch(`${API_BASE}/sessions/${sessionId}`, {
        method: 'DELETE',
        headers: getHeaders()
    });
    if (!res.ok) throw new Error('Failed to delete conversation');
}

/**
 * Render a single conversation item
 */
function renderConversationItem(conv) {
    const item = document.createElement('div');
    item.className = 'conversation-item';
    item.dataset.sessionId = conv.session_id;
    item.dataset.agent = conv.agent || '';
    item.dataset.model = conv.model || '';
    item.dataset.collection = conv.collection || '';

    // Mark active if this is the current session
    if (conv.session_id === state.sessionId) {
        item.classList.add('active');
    }

    // Add agent/collection/model header if present
    if (conv.agent || conv.collection || conv.model) {
        const header = document.createElement('div');
        header.className = 'conversation-header';
        let label, type;
        if (conv.agent) {
            label = conv.agent;
            type = 'agent';
        } else if (conv.collection) {
            label = getCollectionTitleByName(conv.collection) || conv.collection;
            type = 'collection';
        } else {
            // Show the actual model that was last used in this conversation
            // (the friendly display name when we can resolve it).
            label = getModelDisplayName(conv.model) || conv.model;
            type = 'model';
        }
        header.innerHTML = `<span class="conversation-${type}-badge">${label}</span>`;
        item.appendChild(header);
    }

    const preview = document.createElement('div');
    preview.className = 'conversation-preview';
    preview.textContent = conv.preview || 'Empty conversation';

    const meta = document.createElement('div');
    meta.className = 'conversation-meta';

    const time = document.createElement('span');
    time.className = 'conversation-time';
    time.textContent = formatRelativeTime(conv.updated_at);

    const count = document.createElement('span');
    count.className = 'conversation-count';
    count.textContent = `${conv.message_count} msg${conv.message_count !== 1 ? 's' : ''}`;

    meta.appendChild(time);
    meta.appendChild(count);

    const deleteBtn = document.createElement('button');
    deleteBtn.className = 'conversation-delete';
    deleteBtn.innerHTML = '&times;';
    deleteBtn.title = 'Delete conversation';
    deleteBtn.onclick = (e) => {
        e.stopPropagation();
        showDeleteConfirmation(item);
    };

    // Delete confirmation content (hidden by default)
    const confirmContent = document.createElement('div');
    confirmContent.className = 'confirm-content';

    const confirmText = document.createElement('span');
    confirmText.className = 'confirm-text';
    confirmText.textContent = 'Delete?';

    const confirmButtons = document.createElement('div');
    confirmButtons.className = 'confirm-buttons';

    const yesBtn = document.createElement('button');
    yesBtn.className = 'confirm-yes';
    yesBtn.textContent = 'Yes';
    yesBtn.onclick = async (e) => {
        e.stopPropagation();
        await deleteConversation(conv.session_id);
    };

    const noBtn = document.createElement('button');
    noBtn.className = 'confirm-no';
    noBtn.textContent = 'No';
    noBtn.onclick = (e) => {
        e.stopPropagation();
        clearConfirmations(item);
    };

    confirmButtons.appendChild(yesBtn);
    confirmButtons.appendChild(noBtn);
    confirmContent.appendChild(confirmText);
    confirmContent.appendChild(confirmButtons);

    // Load confirmation content (hidden by default)
    const loadConfirmContent = document.createElement('div');
    loadConfirmContent.className = 'load-confirm-content';

    const loadConfirmText = document.createElement('span');
    loadConfirmText.className = 'confirm-text';
    loadConfirmText.textContent = 'Load this conversation?';

    const loadConfirmButtons = document.createElement('div');
    loadConfirmButtons.className = 'confirm-buttons';

    const loadYesBtn = document.createElement('button');
    loadYesBtn.className = 'confirm-load';
    loadYesBtn.textContent = 'Load';
    loadYesBtn.onclick = async (e) => {
        e.stopPropagation();
        await loadConversation(conv.session_id);
    };

    const loadNoBtn = document.createElement('button');
    loadNoBtn.className = 'confirm-no';
    loadNoBtn.textContent = 'Cancel';
    loadNoBtn.onclick = (e) => {
        e.stopPropagation();
        clearConfirmations(item);
    };

    loadConfirmButtons.appendChild(loadYesBtn);
    loadConfirmButtons.appendChild(loadNoBtn);
    loadConfirmContent.appendChild(loadConfirmText);
    loadConfirmContent.appendChild(loadConfirmButtons);

    item.appendChild(preview);
    item.appendChild(meta);
    item.appendChild(deleteBtn);
    item.appendChild(confirmContent);
    item.appendChild(loadConfirmContent);

    // Click to show load confirmation (only if not already in confirm mode)
    item.onclick = () => {
        if (!item.classList.contains('confirming-delete') && !item.classList.contains('confirming-load')) {
            showLoadConfirmation(item);
        }
    };

    return item;
}

/**
 * Clear all confirmation states from an item
 */
function clearConfirmations(item) {
    item.classList.remove('confirming-delete', 'confirming-load');
}

/**
 * Show delete confirmation on an item
 */
function showDeleteConfirmation(item) {
    // Remove confirmation from any other items
    listContainer?.querySelectorAll('.conversation-item').forEach(el => {
        clearConfirmations(el);
    });
    item.classList.add('confirming-delete');
}

/**
 * Show load confirmation on an item
 */
function showLoadConfirmation(item) {
    // Remove confirmation from any other items
    listContainer?.querySelectorAll('.conversation-item').forEach(el => {
        clearConfirmations(el);
    });
    item.classList.add('confirming-load');
}

/**
 * Delete a conversation with fade animation
 */
async function deleteConversation(sessionId) {
    try {
        // Find the item element
        const item = listContainer?.querySelector(`[data-session-id="${sessionId}"]`);

        // Start fade animation
        if (item) {
            item.classList.add('deleting');
        }

        // Delete from server
        await deleteConversationApi(sessionId);

        // If we deleted the current session, clear the chat
        if (sessionId === state.sessionId) {
            state.sessionId = null;
            clearChat();
        }

        // Wait for animation to complete, then remove element
        if (item) {
            await new Promise(resolve => setTimeout(resolve, 250));
            item.remove();
        }

        // Update hasMore flag if needed (don't refresh whole list to preserve scroll)
        // If list is now empty, show empty state
        if (listContainer && listContainer.querySelectorAll('.conversation-item').length === 0) {
            listContainer.innerHTML = '<div class="conversations-empty">No conversations yet</div>';
        }
    } catch (e) {
        console.error('[Conversations] Delete error:', e);
        // Remove deleting class on error
        const item = listContainer?.querySelector(`[data-session-id="${sessionId}"]`);
        if (item) item.classList.remove('deleting');
    }
}

/**
 * Load a conversation into the chat
 */
/**
 * Does this stored message body represent a failed turn?
 *
 * Kept beside restoreRetryAffordances so the two restore paths (clicking a
 * conversation, and reloading the page) cannot disagree about what an error
 * looks like.
 */
export function isErrorMessageContent(content) {
    const c = content || '';
    return c.startsWith('\u26a0\ufe0f Error') ||
           c.startsWith('Error:') ||
           c.includes('Response interrupted:');
}

/**
 * Put Retry / Change Model / Delete back on a failed turn after a reload.
 *
 * An error message persists to the database, but its recovery controls were
 * only ever attached live, at the moment the stream failed. Reload the page
 * and the error was still there with no way to act on it, which strands the
 * user on a turn they can neither retry nor remove.
 *
 * addRetryButton also bails out unless state.lastSentMessage is populated,
 * and that is empty on a fresh page, so the preceding user message has to be
 * restored as the retry target first.
 */
export function restoreRetryAffordances({
    isError, lastUserMsg, lastUserMsgEl, lastAssistantEl, lastAssistantId,
}) {
    if (!isError || !lastUserMsg || !lastAssistantEl) return;
    setLastSentMessage(lastUserMsg.message, lastUserMsg.attachments, lastUserMsgEl);
    addRetryButton(lastAssistantEl, false, lastAssistantId);
}

export async function loadConversation(sessionId) {
    try {
        // Fetch conversation history first - we need this for both metadata and messages
        const history = await fetchConversationHistory(sessionId);

        if (!history || !history.messages) {
            console.error('[Conversations] Invalid history response:', history);
            showToast('That conversation came back empty — nothing to load.', 'error', 5000);
            return;
        }

        // Get conversation metadata from DOM item if available, otherwise use API data
        const item = listContainer?.querySelector(`[data-session-id="${sessionId}"]`);
        const convAgent = item?.dataset?.agent || history.agent || '';
        const convModel = item?.dataset?.model || history.model || '';
        const convCollection = item?.dataset?.collection || history.collection || '';
        // Remote-source conversations (e.g. wp-notes dictations from the
        // firefly-ragsmith plugin) don't run through /chat — they're just
        // a transcript log. Warming a model for them would needlessly evict
        // whatever the user has loaded for actual chat use, so we mark them
        // here and skip the VRAM-load branches below.
        const isRemoteSourceConv = (history.source === 'wp-notes');


        // Determine model tier from conversation's model name (for passing to agent/collection loaders)
        const convModelTier = convModel ? (findTierByModelName(convModel) || convModel) : null;

        // Check if we need to load a different agent
        const needsAgentLoad = convAgent && convAgent !== state.loadedAgent;

        // Close menu first (directly manipulate DOM to avoid circular import)
        const menuPanel = document.getElementById('menu-panel');
        const menuOverlay = document.getElementById('menu-overlay');
        if (menuPanel) menuPanel.classList.remove('open');
        if (menuOverlay) menuOverlay.classList.remove('active');
        document.body.style.overflow = '';

        // Load the agent if needed
        if (needsAgentLoad) {
            // Update agent select
            const menuElements = getMenuElements();
            const agentSelect = menuElements.agentSelect;
            const hiddenAgentSelect = document.getElementById('agent-select');

            if (agentSelect) agentSelect.value = convAgent;
            if (hiddenAgentSelect) hiddenAgentSelect.value = convAgent;

            // Show loading overlay
            const loadingOverlay = document.getElementById('loading-overlay');
            const loadingTitle = document.getElementById('loading-title');
            const progressFill = document.getElementById('progress-fill');
            const progressText = document.getElementById('progress-text');

            if (loadingOverlay) {
                loadingTitle.textContent = `Loading ${convAgent}`;
                progressFill.style.width = '0%';
                progressText.textContent = 'Initializing...';
                loadingOverlay.classList.add('active');
            }

            try {
                const finalData = await loadAgent(convAgent, (loaded, total, status) => {
                    const percent = Math.round((loaded / total) * 100);
                    if (progressFill) progressFill.style.width = `${percent}%`;
                    if (progressText) progressText.textContent = status || `${loaded} / ${total} KBs`;
                }, convModelTier);

                if (loadingOverlay) loadingOverlay.classList.remove('active');

                if (finalData) {
                    state.loadedAgent = convAgent;
                    state.currentAgent = convAgent;
                    state.agentSettings = finalData.settings;
                    state.agentKbCount = finalData.kb_count || 0;
                    // Clear collection state since agent is loaded
                    state.loadedCollection = null;
                    state.loadedCollectionTitle = null;
                    state.loadedCollectionDescription = null;
                    state.currentCollection = '';
                    hideCollectionHeader();

                    // Handle narrator mode settings from agent
                    state.narratorPaused = false;  // Reset pause on conversation switch
                    if (finalData.settings?.narrator_mode) {
                        state.narratorMode = true;
                        // Try to restore saved narrator progress for this session
                        const savedProgress = loadNarratorProgress(sessionId);
                        if (savedProgress) {
                            state.narratorState = savedProgress;
                        } else {
                            // No local progress - fetch from server
                            let chunkId = 1;
                            let book = finalData.settings.narrator_starting_book || null;
                            let kbPath = null;
                            try {
                                const serverProgress = await fetchNarratorProgress(convAgent);
                                if (serverProgress?.current_position) {
                                    chunkId = serverProgress.current_position.chunk_id || 1;
                                    kbPath = serverProgress.current_position.kb || null;
                                }
                                if (serverProgress?.current_book) {
                                    book = serverProgress.current_book;
                                }
                            } catch (e) {
                                console.error('[Conversations] Failed to fetch narrator progress:', e);
                            }
                            state.narratorState = {
                                book: book,
                                kb_path: kbPath,
                                chunk_id: chunkId,
                                chunk_batch: state.narratorState.chunk_batch || 1,
                                narration_length: state.narratorState.narration_length ?? 1
                            };
                        }
                    } else {
                        state.narratorMode = false;
                    }

                    // Populate tool toggles in plus menu
                    window.populateToolToggles?.(finalData.settings?.tools || [], finalData.settings);

                    updateModeIndicators(convAgent, null, state.narratorMode, state.narratorState.chunk_id);
                    updateMenuLoadButton(convAgent, convAgent);
                    updateHeaderStatus();
                    window.updateNarratorMenuSection?.();
                    // Update voice button icon and web search button for narrator mode
                    import('./voice.js').then(({ updateVoiceBtnIcon }) => {
                        const effectiveNarrator = state.narratorMode && !state.narratorPaused;
                        updateVoiceBtnIcon(effectiveNarrator, !!state.agentSettings?.narrator_verbatim);
                    }).catch(() => {});
                    window.updateWebSearchButtonState?.();
                }
            } catch (e) {
                console.error('[Conversations] Agent load error:', e);
                if (loadingOverlay) loadingOverlay.classList.remove('active');
            }
        }

        // Load the collection if needed (mutually exclusive with agent)
        // Note: convCollection is already defined from API/DOM at top of function
        const needsCollectionLoad = convCollection && convCollection !== state.loadedCollection && !convAgent;

        if (needsCollectionLoad) {
            // Update collection select
            const menuElements = getMenuElements();
            const collectionSelect = menuElements.collectionSelect;

            if (collectionSelect) {
                collectionSelect.value = convCollection;
                // Get the title from the option's data attribute
                const selectedOption = collectionSelect.selectedOptions[0];
                const collectionTitle = selectedOption?.dataset?.collectionTitle || convCollection;

                // Show loading overlay
                const loadingOverlay = document.getElementById('loading-overlay');
                const loadingTitle = document.getElementById('loading-title');
                const progressFill = document.getElementById('progress-fill');
                const progressText = document.getElementById('progress-text');

                if (loadingOverlay) {
                    loadingTitle.textContent = `Loading ${collectionTitle}`;
                    progressFill.style.width = '0%';
                    progressText.textContent = 'Initializing...';
                    loadingOverlay.classList.add('active');
                }

                try {
                    const finalData = await loadCollection(convCollection, (loaded, total, status) => {
                        const percent = Math.round((loaded / total) * 100);
                        if (progressFill) progressFill.style.width = `${percent}%`;
                        if (progressText) progressText.textContent = status || `${loaded} / ${total} KBs`;
                    }, convModelTier || state.loadedModel);

                    if (loadingOverlay) loadingOverlay.classList.remove('active');

                    if (finalData) {
                        state.loadedCollection = convCollection;
                        state.loadedCollectionTitle = finalData.title || collectionTitle;
                        state.loadedCollectionDescription = finalData.description || null;
                        state.currentCollection = convCollection;
                        state.loadedAgent = null;  // Clear agent since collection is loaded
                        state.currentAgent = '';
                        state.narratorMode = false;
                        state.narratorPaused = false;

                        updateModeIndicators(null, state.loadedCollectionTitle, false, null);
                        updateMenuLoadButton(convCollection, state.loadedCollectionTitle);
                        updateHeaderStatus();
                    }
                } catch (e) {
                    console.error('[Conversations] Collection load error:', e);
                    if (loadingOverlay) loadingOverlay.classList.remove('active');
                }
            }
        }

        // If conversation has no agent or collection, clear any loaded state
        if (!convAgent && !convCollection) {
            state.loadedAgent = null;
            state.currentAgent = '';
            state.loadedCollection = null;
            state.loadedCollectionTitle = null;
            state.loadedCollectionDescription = null;
            state.currentCollection = '';
            state.narratorMode = false;
            state.narratorPaused = false;
            state.agentSettings = null;
            state.agentKbCount = 0;
            hideCollectionHeader();

            // Clear dropdown selections
            const menuElements = getMenuElements();
            if (menuElements.agentSelect) menuElements.agentSelect.value = '';
            if (menuElements.collectionSelect) menuElements.collectionSelect.value = '';
            const hiddenAgentSelect = document.getElementById('agent-select');
            if (hiddenAgentSelect) hiddenAgentSelect.value = '';

            updateModeIndicators(null, null, false, null);
            updateAgentDisplay(null);
            updateMenuLoadButton();
            updateHeaderStatus();
            // Re-enable web search and reset plus menu (narrator disables these)
            window.updateWebSearchButtonState?.();
            window.populateToolToggles?.([], null);
            // Reset voice button to mic-only (no narrator)
            import('./voice.js').then(({ updateVoiceBtnIcon }) => {
                updateVoiceBtnIcon(false, false);
            }).catch(() => {});
        }

        // Set model if specified (convModel is the actual model name from DB)
        if (convModel) {
            const menuElements = getMenuElements();
            const modelSelect = menuElements.modelSelect;
            const hiddenModelSelect = document.getElementById('model-select');

            // Try to find tier by model name
            const tierValue = findTierByModelName(convModel);

            // Check if this is a cloud/API model (Anthropic, Groq, or OpenRouter)
            // Use server-provided provider as source of truth, fall back to dropdown/heuristics
            const ANTHROPIC_TIERS = ['haiku', 'sonnet', 'opus'];
            const OPENROUTER_TIERS = ['r1', 'deepseek'];
            const serverProvider = history.provider || null;
            // Find the matching option to get its provider (fallback)
            let matchedProvider = serverProvider;
            if (!matchedProvider && modelSelect) {
                for (const opt of modelSelect.options) {
                    if (opt.value === tierValue || opt.dataset.modelName === convModel) {
                        matchedProvider = opt.dataset.provider;
                        break;
                    }
                }
            }
            const isAnthropicModel = matchedProvider === 'anthropic' || convModel.startsWith('claude-') || ANTHROPIC_TIERS.includes(convModel);
            const isOpenAIModel = matchedProvider === 'openai' || convModel.startsWith('gpt-') || ['o3', 'o3-mini', 'o4-mini'].includes(convModel);
            const isGroqModel = matchedProvider === 'groq' || convModel.startsWith('groq:');
            const isOpenRouterModel = !isGroqModel && !isOpenAIModel && (matchedProvider === 'openrouter' || convModel.includes('/') || OPENROUTER_TIERS.includes(convModel));
            const isApiModel = isAnthropicModel || isOpenAIModel || isOpenRouterModel || isGroqModel;

            if (tierValue) {
                // Found matching tier - update dropdown
                if (modelSelect) modelSelect.value = tierValue;
                if (hiddenModelSelect) hiddenModelSelect.value = tierValue;

                const isApiTier = isAnthropicModel || isOpenAIModel || isGroqModel || isOpenRouterModel;

                // For API tiers (provider models), always update state and display
                // since they don't need VRAM loading
                if (isApiTier) {
                    const provider = isAnthropicModel ? 'anthropic' : isOpenAIModel ? 'openai' : (isGroqModel ? 'groq' : 'openrouter');
                    state.loadedModel = tierValue;
                    state.loadedProvider = provider;
                    state.selectedModel = null;  // Clear any pending selection
                    const selectedOption = modelSelect?.selectedOptions[0];
                    const displayName = selectedOption?.dataset?.model || tierValue;
                    updateCurrentModelDisplay(tierValue, displayName, provider, true);
                }
                // For local models - always set state.loadedModel to match conversation
                // If agent/collection is loading, they will warm the model as needed
                else {
                    // A conversation remembers the model it was last used with,
                    // and re-pinning the header to it is right until the crew
                    // serving that model is stood down. Then the dead name
                    // survives every refresh and Retry keeps resending it,
                    // because Retry reads the header, not the failed turn.
                    // Heal to something the pool is actually serving.
                    // See healUnservableModel.
                    let effectiveTier = tierValue;
                    const healed = healUnservableModel(tierValue, 'local');
                    if (healed) {
                        effectiveTier = healed.value;
                        if (modelSelect) modelSelect.value = effectiveTier;
                        if (hiddenModelSelect) hiddenModelSelect.value = effectiveTier;
                        showToast(
                            `${convModel} is not being served right now. Using ${healed.modelName}.`,
                            'info'
                        );
                    }
                    // A heal can land on the cloud lane, which is the pool's
                    // own answer for "what should chat use". Nothing about it
                    // is local: it has no VRAM to warm, and calling it local
                    // would route the turn at a worker that does not serve it.
                    const healedProvider = healed
                        ? (providerForValue(effectiveTier)
                           || (effectiveTier.includes('/') ? 'openrouter' : 'local'))
                        : 'local';
                    const needsVramLoad = healedProvider === 'local'
                        && effectiveTier !== state.loadedModel;

                    // Always update state.loadedModel to match conversation's model
                    state.loadedModel = effectiveTier;
                    state.loadedProvider = healedProvider;
                    state.selectedModel = null;  // Clear any pending selection

                    // Only do VRAM loading if model changed AND no agent/collection
                    // (agent/collection loading will warm the model as needed).
                    // Skip entirely for remote-source conversations — see comment
                    // at isRemoteSourceConv definition above.
                    if (needsVramLoad && !convAgent && !convCollection && !isRemoteSourceConv) {
                        const overlay = document.getElementById('connecting-overlay');
                        const text = overlay?.querySelector('.connecting-text');
                        if (overlay) {
                            overlay.classList.remove('hidden');
                            const selectedOption = modelSelect?.selectedOptions[0];
                            const modelName = selectedOption?.dataset?.model || tierValue;
                            if (text) text.textContent = `Loading ${modelName}`;
                        }

                        try {
                            await loadModel(effectiveTier);
                        } catch (e) {
                            console.error('[Conversations] Model preload failed:', e);
                        }

                        if (overlay) overlay.classList.add('hidden');
                    }

                    // Update the current model display
                    const selectedOption = modelSelect?.selectedOptions[0];
                    const displayName = selectedOption?.dataset?.model || effectiveTier;
                    updateCurrentModelDisplay(effectiveTier, displayName, 'local', true);
                }
            } else if (isApiModel) {
                // No tier match but it's an API model (OpenRouter or Anthropic not in tiers)
                // Update state and display with the raw model name
                const provider = isAnthropicModel ? 'anthropic' : (isOpenAIModel ? 'openai' : (isGroqModel ? 'groq' : 'openrouter'));
                state.loadedModel = convModel;
                state.loadedProvider = provider;
                state.selectedModel = null;  // Clear any pending selection

                // Extract display name from model ID
                let displayName = getModelDisplayName(convModel) || convModel;
                if (displayName === convModel) {
                    // Fallback: format raw model name
                    if (convModel.includes('/')) {
                        // OpenRouter format: "google/gemini-2.0-flash-exp:free" -> "Gemini 2.0 Flash Exp"
                        const parts = convModel.split('/');
                        if (parts.length > 1) {
                            displayName = parts[1].split(':')[0]
                                .replace(/-/g, ' ')
                                .replace(/\b\w/g, c => c.toUpperCase());
                        }
                    } else if (!convModel.startsWith('claude-')) {
                        displayName = convModel.charAt(0).toUpperCase() + convModel.slice(1);
                    }
                }

                updateCurrentModelDisplay(convModel, displayName, provider, true);
            }

            updateHeaderStatus();
        }

        // History was already fetched at the top of function
        // Set the session ID
        state.sessionId = sessionId;

        // Clear current chat
        clearChat();

        // Show collection header if this is a collection conversation
        if (state.loadedCollection && state.loadedCollectionTitle) {
            showCollectionHeader(state.loadedCollectionTitle, state.loadedCollectionDescription);
        }

        // Determine the assistant name for messages
        // Use agent, collection title, or fall back to model name from conversation
        const collectionTitle = state.loadedCollectionTitle || getCollectionTitleByName(convCollection);
        let assistantName = convAgent || collectionTitle || convCollection || null;
        // Format hyphenated agent names: "search-agent" → "Search Agent"
        if (assistantName && assistantName.includes('-')) {
            assistantName = assistantName.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
        }

        // If no agent/collection, use model name from the conversation (not current dropdown)
        if (!assistantName && convModel) {
            // Extract model family name from the conversation's model
            // e.g., "meta-llama/llama-3.3-70b-instruct:free" -> "Llama"
            // e.g., "qwen2.5:3b" -> "Qwen"
            let modelName = convModel;
            if (convModel.includes('/')) {
                // OpenRouter format: provider/model-name
                modelName = convModel.split('/')[1] || convModel;
            }
            // Extract the model family (first word, capitalize)
            const match = modelName.match(/^([a-zA-Z]+)/);
            if (match) {
                assistantName = match[1].charAt(0).toUpperCase() + match[1].slice(1).toLowerCase();
            } else {
                assistantName = modelName;
            }
        }

        // Final fallback
        if (!assistantName) {
            assistantName = 'Ragsmith';
        }

        // Model name for message footers (resolved by server to match live chat format)
        const footerModelName = history.display_model || null;

        // Render all messages without scrolling (including citations)
        let lastUserMsg = null;
        let lastUserMsgEl = null;
        let lastAssistantEl = null;
        let lastAssistantId = null;
        let lastAssistantIsError = false;
        // Track user messages so we can post-pass identify orphans (a user
        // message with no assistant response after it — usually because the
        // user cancelled mid-generation).
        const userMsgEntries = [];  // { msg, el, indexInHistory }

        for (const msg of history.messages) {
            // Skip system prompt trigger messages (internal markers)
            if (msg.role === 'user' && msg.content.startsWith('[SYSTEM:')) {
                continue;
            }

            // Fact-ingest chips: stored as role='system' messages with
            // is_fact_ingest metadata. Render via the dedicated chip
            // component (no user/assistant bubble involved).
            if (msg.role === 'system' && msg.metadata && msg.metadata.is_fact_ingest) {
                renderFactIngestChip({
                    chip_message_id: msg.id,
                    filename: msg.metadata.filename || 'document',
                    fact_count: msg.metadata.fact_count || 0,
                    facts: msg.metadata.facts || [],
                    source_marker: msg.metadata.source_marker,
                }, { sessionId, noScroll: true });
                continue;
            }

            // Recording ingests: same idea, richer card — it carries the
            // recording's parts so they can be opened from the chat.
            if (msg.role === 'system' && msg.metadata && msg.metadata.is_recording_ingest) {
                renderRecordingIngestCard({
                    ...msg.metadata,
                    chip_message_id: msg.id,
                }, { sessionId, noScroll: true });
                continue;
            }

            // Note-synthesis markers: role='system' messages with
            // is_note_synthesis metadata. These are internal bookkeeping for
            // incremental synthesis (they stash the latest synthesis text so
            // the next run can merge into it) — the synthesis itself was
            // already delivered to the user as the assistant message. They're
            // NOT meant to be displayed; without this skip the marker falls
            // through to the generic render path, and since markdown is only
            // rendered for role='assistant', the marker's content shows as
            // raw literal markdown (#, ##, *) — the "weird hashtag block" on
            // reload.
            if (msg.role === 'system' && msg.metadata && msg.metadata.is_note_synthesis) {
                continue;
            }
            // Compaction markers: the summary that replaced the turns above
            // it in the model's view, shown as a divider that opens to it.
            if (msg.role === 'system' && msg.metadata && msg.metadata.is_compaction) {
                renderCompactionDivider(msg);
                continue;
            }

            const options = {
                render: true,
                noScroll: true,
                citations: msg.citations || null,
                attachments: msg.attachments || null,
                messageId: msg.id || null,  // Include message ID for deletion
                toolsUsed: msg.tools_used || null,  // Tools used for this response
                stepLog: msg.step_log || null  // Step accordion data
            };
            // Dictation messages: detect via metadata flag and render with
            // dictation styling (assistant-like with badge + Edit footer)
            if (msg.metadata && msg.metadata.is_dictation) {
                options.isDictation = true;
            }
            // Ingested-document messages: user-side bubble with filename
            // chip + click-to-expand text body. Detected via metadata flag
            // set by /document/extract. We let the addMessage path render
            // the document UI (chip + toggle + body), so msg.content
            // becomes the document body text (which addMessage uses as the
            // expandable text).
            if (msg.metadata && msg.metadata.is_document && msg.role === 'user') {
                options.isDocument = true;
                options.filename = msg.metadata.filename || 'document';
            }
            // Pass narrator metadata so the mode indicator can compute "chunk N next"
            // and so the delete-revert logic on these messages works after a load.
            if (msg.narrator_chunk_id) {
                options.narratorChunkId = msg.narrator_chunk_id;
                options.narratorBook = msg.narrator_book;
                options.narratorSection = msg.narrator_section;
                options.narratorTotalChunks = msg.narrator_total_chunks;
                options.narratorKbPath = msg.narrator_kb_path;
                options.narratorChunkCount = msg.narrator_chunk_count || 1;
            }
            // Add header for messages
            if (msg.role === 'assistant' && assistantName) {
                options.header = assistantName;
            } else if (msg.role === 'user' && !options.isDictation) {
                options.header = 'You';
            }
            // Separate thinking content from assistant messages before rendering
            let displayContent = msg.content;
            let thinkingText = null;
            if (msg.role === 'assistant' && hasThinkingContent(msg.content)) {
                const { thinking, response } = separateThinkingContent(msg.content);
                displayContent = response;
                thinkingText = thinking;
            }

            const msgEl = addMessage(msg.role, displayContent, options);

            // Add collapsed thinking section to assistant messages
            if (thinkingText && msgEl) {
                addThinkingToMessage(msgEl, thinkingText, true);
            }

            // Add model footer to assistant messages (matches live chat format)
            if (msg.role === 'assistant' && footerModelName && msgEl) {
                addMetaToMessage(msgEl, `Model: ${footerModelName}`);
            }

            // Render image search results if stored with this message
            if (msg.role === 'assistant' && msg.image_results && msg.image_results.length > 0 && msgEl) {
                showImageSearchResults(msgEl, msg.image_results);
            }

            // Restore per-message analytics footer
            if (msg.role === 'assistant' && msg.metadata && msgEl) {
                addAnalyticsToMessage(msgEl, msg.metadata);
            }

            // Track last user message for potential retry
            if (msg.role === 'user') {
                lastUserMsg = { message: msg.content, attachments: msg.attachments || [] };
                lastUserMsgEl = msgEl;
                // Also track all user messages for orphan detection below.
                // Skip dictations (standalone by design) and document bubbles
                // (the analyze flow saves user-doc immediately followed by a
                // separate user instruction message, so the doc bubble's
                // "next message" is a user message, not assistant — would
                // incorrectly trip the orphan check).
                if (!options.isDictation && !options.isDocument) {
                    userMsgEntries.push({ msg, el: msgEl });
                }
            }

            // Track last assistant message to check for error
            if (msg.role === 'assistant') {
                lastAssistantEl = msgEl;
                lastAssistantId = msg.id || null;
                // Detect various error message formats:
                // - "⚠️ Error: ..." (streaming error with emoji)
                // - "Error: ..." (exception error without emoji)
                // - "Response interrupted: ..." (partial streaming error)
                lastAssistantIsError = isErrorMessageContent(msg.content);
            }
        }

        // Decorate footers with any download links this conversation already
        // owns — one call for the whole transcript, after it is on screen.
        window.loadSessionPermalinks?.();

        // If last assistant message was an error, set up retry state
        restoreRetryAffordances({
            isError: lastAssistantIsError,
            lastUserMsg,
            lastUserMsgEl,
            lastAssistantEl,
            lastAssistantId,
        });

        // Orphan detection: any user message NOT immediately followed by an
        // assistant message is orphaned (the user probably cancelled before
        // generation completed). Attach a delete button so they can clean
        // it up. Walk the messages array in order — for each user entry,
        // look ahead to find the next non-system message; if it's not an
        // assistant, this one is orphaned.
        for (const { msg, el } of userMsgEntries) {
            if (!msg.id || !el) continue;
            const idx = history.messages.findIndex(m => m.id === msg.id);
            if (idx === -1) continue;
            let hasResponse = false;
            for (let j = idx + 1; j < history.messages.length; j++) {
                const next = history.messages[j];
                // Skip system-marker user messages when walking
                if (next.role === 'user' && next.content?.startsWith('[SYSTEM:')) continue;
                hasResponse = (next.role === 'assistant');
                break;
            }
            if (!hasResponse) {
                attachOrphanDeleteButton(el, sessionId, msg.id);
            }
        }

        // Scroll: in narrator mode, jump to the LAST narrator chunk message
        // (not just bottom) — preserves the user's last position. Otherwise
        // scroll to bottom as usual.
        const chatContainer = document.getElementById('chat-container');
        if (chatContainer) {
            if (state.narratorMode) {
                const narratorMessages = chatContainer.querySelectorAll(
                    '.message.assistant[data-narrator-chunk-id]'
                );
                if (narratorMessages.length > 0) {
                    const last = narratorMessages[narratorMessages.length - 1];
                    requestAnimationFrame(() => {
                        last.scrollIntoView({ behavior: 'instant', block: 'start' });
                    });
                } else {
                    chatContainer.scrollTop = chatContainer.scrollHeight;
                }
            } else {
                chatContainer.scrollTop = chatContainer.scrollHeight;
            }
        }

        // Update active state in list
        updateActiveConversation(sessionId);

        // Save session state for persistence across refresh - always use state.loadedModel
        saveSessionStateWithModel(state.loadedModel);

    } catch (e) {
        // A silent catch here made the Load button look dead: it threw, wrote
        // one line to a console nobody had open, and left the panel exactly as
        // it was. Whatever the cause, the user has to be told the click failed.
        console.error('[Conversations] Load error:', e, e?.stack);
        showToast(`Could not load conversation: ${e?.message || e}`, 'error', 6000);
    }
}

/**
 * Update active conversation highlight
 */
function updateActiveConversation(sessionId) {
    if (!listContainer) return;

    // Remove active from all
    listContainer.querySelectorAll('.conversation-item').forEach(item => {
        item.classList.remove('active');
    });

    // Add active to current
    const activeItem = listContainer.querySelector(`[data-session-id="${sessionId}"]`);
    if (activeItem) {
        activeItem.classList.add('active');
    }
}

/**
 * Clear active conversation highlight (when starting a new session)
 */
export function clearActiveConversation() {
    if (!listContainer) return;

    listContainer.querySelectorAll('.conversation-item').forEach(item => {
        item.classList.remove('active');
    });
}

/**
 * Render conversations list
 */
function renderConversationsList(conversations, append = false) {
    if (!listContainer) return;

    if (!append) {
        listContainer.innerHTML = '';
    }

    if (conversations.length === 0 && !append) {
        const empty = document.createElement('div');
        empty.className = 'conversations-empty';
        empty.textContent = hasActiveFilters() ? 'No matching conversations' : 'No conversations yet';
        listContainer.appendChild(empty);
        return;
    }

    conversations.forEach(conv => {
        const item = renderConversationItem(conv);
        listContainer.appendChild(item);
    });
}

/**
 * Show loading state
 */
function showLoading() {
    if (!listContainer) return;
    listContainer.innerHTML = `
        <div class="conversations-loading">
            <div class="spinner"></div>
            <span>Loading...</span>
        </div>
    `;
}

/**
 * Refresh conversations list (full reload)
 */
export async function refreshConversations() {
    if (isLoading) return;
    isLoading = true;
    currentOffset = 0;

    // Spin the refresh button
    if (refreshBtn) refreshBtn.classList.add('spinning');

    try {
        // Filter module already shows "Searching..." spinner; skip redundant "Loading..."
        if (!isFilterLoading()) showLoading();
        const data = await fetchConversations(0, PAGE_SIZE);

        // Ensure filter spinner is visible long enough for user feedback
        await waitForMinSpinner();

        hasMore = data.has_more;
        renderConversationsList(data.sessions, false);

        // Show/hide load more button
        if (loadMoreBtn) {
            loadMoreBtn.style.display = hasMore ? 'block' : 'none';
        }
    } catch (e) {
        console.error('[Conversations] Refresh error:', e);
        if (listContainer) {
            listContainer.innerHTML = '<div class="conversations-empty">Failed to load</div>';
        }
    } finally {
        isLoading = false;
        if (refreshBtn) refreshBtn.classList.remove('spinning');
    }
}

/**
 * Load more conversations (pagination)
 */
async function loadMore() {
    if (isLoading || !hasMore) return;
    isLoading = true;

    try {
        currentOffset += PAGE_SIZE;
        const data = await fetchConversations(currentOffset, PAGE_SIZE);
        hasMore = data.has_more;
        renderConversationsList(data.sessions, true);

        // Show/hide load more button
        if (loadMoreBtn) {
            loadMoreBtn.style.display = hasMore ? 'block' : 'none';
        }
    } catch (e) {
        console.error('[Conversations] Load more error:', e);
        currentOffset -= PAGE_SIZE; // Revert offset on error
    } finally {
        isLoading = false;
    }
}

/**
 * Initialize conversations module
 */
export function initConversations() {
    listContainer = document.getElementById('conversations-list');
    loadMoreBtn = document.getElementById('load-more-btn');
    refreshBtn = document.getElementById('refresh-conversations-btn');

    // Initialize filter accordion
    initConvFilters(() => {
        currentOffset = 0;
        refreshConversations();
    });

    // Set up event listeners
    if (refreshBtn) {
        refreshBtn.onclick = () => refreshConversations();
    }

    if (loadMoreBtn) {
        loadMoreBtn.onclick = () => loadMore();
    }

    // Initial load
    refreshConversations();
}

/**
 * Called when a new message is sent to update active state
 */
export function onNewSession(sessionId) {
    // Update active state in list if needed
    updateActiveConversation(sessionId);
}
